/**
 * Thin fetch wrapper for talking to the V2 PHP JSON endpoints.
 * Uses the same-origin session cookie for authentication.
 */

const API_BASE = "/v2/app/api";

export class ApiError extends Error {
    constructor(public status: number, message: string, public payload?: unknown) {
        super(message);
        this.name = "ApiError";
    }
}

import { companyGuard, reportCompanyChanged } from "@/api/companyGuard";

let leavingForLogin = false;

/** The start of the last answer that claimed to be JSON and was not (for a message that has to say what came back). */
let lastUnreadable = "";
export const unreadableAnswer = () => lastUnreadable;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const res = await fetch(API_BASE + path, {
        credentials: "same-origin",
        headers: {
            Accept: "application/json",
            ...(init.body && !(init.body instanceof FormData)
                ? { "Content-Type": "application/json" }
                : {}),
            ...(companyGuard.expected ? { "X-Company-Id": String(companyGuard.expected.id) } : {}),
            ...(init.headers ?? {}),
        },
        ...init,
    });

    let payload: any = null;
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("application/json")) {
        const raw = await res.text().catch(() => "");
        try { payload = JSON.parse(raw); } catch {
            // Tolerate an empty body; keep what came for the diagnosis of an answer that is not JSON.
            lastUnreadable = `${res.status} ${ct} len=${raw.length} ${raw.replace(/\s+/g, " ").slice(0, 300)}`;
        }
    } else {
        payload = await res.text().catch(() => null);
    }

    // Not logged in (session expired, or a link opened in a fresh browser): there is
    // nothing to show and no login form in the SPA, so send the user to /v2/login and
    // bring them back to this very page afterwards.
    if (res.status === 401) {
        // Several requests fail together; the first one names the page to come back to.
        if (!leavingForLogin) {
            leavingForLogin = true;
            const here = window.location.pathname + window.location.search;
            window.location.assign("/v2/login?next=" + encodeURIComponent(here));
        }
        return new Promise<T>(() => { /* the page is leaving; never settle */ });
    }

    // Signed in but no company chosen yet (a link opened with a session that has none): the choice
    // is made on the company page, which then brings the user back to this page.
    if (res.status === 400 && payload && typeof payload === "object" && (payload as any).error === "Podjetje ni izbrano.") {
        if (!leavingForLogin) {
            leavingForLogin = true;
            const here = window.location.pathname + window.location.search;
            window.location.assign("/v2/izbira-podjetja?next=" + encodeURIComponent(here));
        }
        return new Promise<T>(() => { /* the page is leaving; never settle */ });
    }

    // The session moved to another company in a different window: stop, tell the user.
    if (res.status === 409 && payload && typeof payload === "object" && (payload as any).code === "company_changed") {
        reportCompanyChanged((payload as any).company);
        return new Promise<T>(() => { /* the notice covers the page; never settle */ });
    }

    if (!res.ok) {
        const msg = (payload && typeof payload === "object" && "error" in payload)
            ? String((payload as any).error)
            : `Request failed (${res.status})`;
        throw new ApiError(res.status, msg, payload);
    }

    return payload as T;
}

export const api = {
    get:  <T>(p: string)              => request<T>(p),
    post: <T>(p: string, body?: any)  => request<T>(p, { method: "POST", body: body == null ? null : JSON.stringify(body) }),
    put:  <T>(p: string, body?: any)  => request<T>(p, { method: "PUT",  body: body == null ? null : JSON.stringify(body) }),
    del:  <T>(p: string)              => request<T>(p, { method: "DELETE" }),
    // multipart: the browser sets the Content-Type (with the boundary) for a FormData body
    postForm: <T>(p: string, body: FormData) => request<T>(p, { method: "POST", body }),
};
